import { createSlice } from "@reduxjs/toolkit";
import {
  fetchBanks,
  addBank,
  updateBank,
  deleteBank,
  fetchDebts,
  addDebt,
  updateDebt,
  deleteDebt,
  fetchExpenses,
  addExpense,
  updateExpense,
  deleteExpense,
  fetchCases,
  addCase,
  updateCase,
  deleteCase,
  fetchCashiers,
  addCashier,
  updateCashier,
  deleteCashier,
} from "./zreportsThunks";

const mapCase = (c) => {
  if (!c || !c.title) return c;
  const parts = c.title.split("-");
  return {
    id: c.id,
    no: parts[0]?.trim() || c.id,
    name: parts[1]?.trim() || c.title,
  };
};

const initialState = {
  kasiyerler: [],
  kasalar: [],
  bankalar: [],
  giderKategoriler: [],
  borcMusteriler: [],
  loading: false,
  error: null,
};

const zreportsSlice = createSlice({
  name: "zreports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // BANKS
    builder.addCase(fetchBanks.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchBanks.fulfilled, (state, action) => {
      state.loading = false;
      state.bankalar = action.payload.map(b => ({ id: b.id, name: b.title, isSelected: b.isSelected || false }));
    });
    builder.addCase(fetchBanks.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder.addCase(addBank.fulfilled, (state, action) => {
      const b = action.payload;
      state.bankalar.push({ id: b.id, name: b.title, isSelected: b.isSelected || false });
    });
    builder.addCase(updateBank.fulfilled, (state, action) => {
      const { id, title } = action.payload;
      const idx = state.bankalar.findIndex((x) => x.id === id);
      if (idx !== -1) state.bankalar[idx].name = title;
    });
    builder.addCase(deleteBank.fulfilled, (state, action) => {
      const id = action.payload;
      state.bankalar = state.bankalar.filter((x) => x.id !== id);
    });

    // DEBTS (Borç Müşteriler)
    builder.addCase(fetchDebts.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchDebts.fulfilled, (state, action) => {
      state.loading = false;
      state.borcMusteriler = action.payload.map(d => ({ id: d.id, name: d.title, phone: "" }));
    });
    builder.addCase(fetchDebts.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder.addCase(addDebt.fulfilled, (state, action) => {
      const d = action.payload;
      state.borcMusteriler.push({ id: d.id, name: d.title, phone: "" });
    });
    builder.addCase(updateDebt.fulfilled, (state, action) => {
      const { id, title } = action.payload;
      const idx = state.borcMusteriler.findIndex((x) => x.id === id);
      if (idx !== -1) state.borcMusteriler[idx].name = title;
    });
    builder.addCase(deleteDebt.fulfilled, (state, action) => {
      const id = action.payload;
      state.borcMusteriler = state.borcMusteriler.filter((x) => x.id !== id);
    });

    // EXPENSES (Gider Kategorileri)
    builder.addCase(fetchExpenses.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchExpenses.fulfilled, (state, action) => {
      state.loading = false;
      state.giderKategoriler = action.payload.map(e => ({ id: e.id, name: e.title }));
    });
    builder.addCase(fetchExpenses.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder.addCase(addExpense.fulfilled, (state, action) => {
      const e = action.payload;
      state.giderKategoriler.push({ id: e.id, name: e.title });
    });
    builder.addCase(updateExpense.fulfilled, (state, action) => {
      const { id, title } = action.payload;
      const idx = state.giderKategoriler.findIndex((x) => x.id === id);
      if (idx !== -1) state.giderKategoriler[idx].name = title;
    });
    builder.addCase(deleteExpense.fulfilled, (state, action) => {
      const id = action.payload;
      state.giderKategoriler = state.giderKategoriler.filter((x) => x.id !== id);
    });

    // CASES (Kasalar)
    builder.addCase(fetchCases.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchCases.fulfilled, (state, action) => {
      state.loading = false;
      state.kasalar = action.payload.map(c => mapCase(c));
    });
    builder.addCase(fetchCases.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder.addCase(addCase.fulfilled, (state, action) => {
      const c = action.payload;
      state.kasalar.push(mapCase(c));
    });
    builder.addCase(updateCase.fulfilled, (state, action) => {
      const { id, title } = action.payload;
      const idx = state.kasalar.findIndex((x) => x.id === id);
      if (idx !== -1) {
        state.kasalar[idx] = mapCase({ id, title });
      }
    });
    builder.addCase(deleteCase.fulfilled, (state, action) => {
      const id = action.payload;
      state.kasalar = state.kasalar.filter((x) => x.id !== id);
    });

    // CASHIERS (Kasiyerler)
    builder.addCase(fetchCashiers.pending, (state) => { state.loading = true; state.error = null; });
    builder.addCase(fetchCashiers.fulfilled, (state, action) => {
      state.loading = false;
      state.kasiyerler = action.payload.map(k => ({ id: k.id, name: k.name, surname: k.surname }));
    });
    builder.addCase(fetchCashiers.rejected, (state, action) => { state.loading = false; state.error = action.payload; });

    builder.addCase(addCashier.fulfilled, (state, action) => {
      const k = action.payload;
      state.kasiyerler.push({ id: k.id, name: k.name, surname: k.surname });
    });
    builder.addCase(updateCashier.fulfilled, (state, action) => {
      const { id, name, surname } = action.payload;
      const idx = state.kasiyerler.findIndex((x) => x.id === id);
      if (idx !== -1) {
        state.kasiyerler[idx].name = name;
        state.kasiyerler[idx].surname = surname;
      }
    });
    builder.addCase(deleteCashier.fulfilled, (state, action) => {
      const id = action.payload;
      state.kasiyerler = state.kasiyerler.filter((x) => x.id !== id);
    });
  },
});

export default zreportsSlice.reducer;
